import { takeLatest, select, takeEvery } from 'redux-saga/effects';
import type { AppState } from '~/reducers';
import callRequestSaga from '~/sagas/utils/request-saga';
import AssetTagsAPI from '~/api/asset-tags-api';
import AssetTagCategoriesAPI from '~/api/asset-tag-categories-api';
import {
  createTagAction,
  createTagCategoryAction,
  deleteTagAction,
  deleteTagCategoryAction,
  updateTagAction,
  updateTagCategoryAction,
} from './actions';
import AssetTagsConstants from './constants';

export function* getAvailableTags() {
  yield* callRequestSaga(AssetTagCategoriesAPI.getAvailableTagCategories, {
    request: AssetTagsConstants.GET_AVAILABLE_TAGS_REQUEST,
    success: AssetTagsConstants.GET_AVAILABLE_TAGS_REQUEST_SUCCESS,
    failure: AssetTagsConstants.GET_AVAILABLE_TAGS_REQUEST_ERROR,
  });
}

export function* createTagCategory(
  action: ReturnType<typeof createTagCategoryAction>
) {
  const { payload } = action;

  yield* callRequestSaga(
    AssetTagCategoriesAPI.createTagCategory,
    {
      request: AssetTagsConstants.CREATE_TAG_CATEGORY_REQUEST,
      success: AssetTagsConstants.CREATE_TAG_CATEGORY_REQUEST_SUCCESS,
      failure: AssetTagsConstants.CREATE_TAG_CATEGORY_REQUEST_ERROR,
      payload: {
        tmpTagCategory: {
          id: `${Math.floor(Math.random() * 100001)}`,
          name: payload.name,
          tags: [],
        },
      },
    },
    payload.name
  );
}

export function* updateTagCategory(
  action: ReturnType<typeof updateTagCategoryAction>
) {
  const { payload } = action;

  yield* callRequestSaga(
    AssetTagCategoriesAPI.updateTagCategory,
    {
      request: AssetTagsConstants.UPDATE_TAG_CATEGORY_REQUEST,
      success: AssetTagsConstants.UPDATE_TAG_CATEGORY_REQUEST_SUCCESS,
      failure: AssetTagsConstants.UPDATE_TAG_CATEGORY_REQUEST_ERROR,
      payload,
    },
    payload.tagCategory.id,
    payload.name
  );
}

export function* deleteTagCategory(
  action: ReturnType<typeof deleteTagCategoryAction>
) {
  const { payload } = action;

  const index = yield select((state: AppState) =>
    state.assetTags.availableTagCategories!.findIndex(
      (cat) => cat.id === payload.tagCategory.id
    )
  );
  yield* callRequestSaga(
    AssetTagCategoriesAPI.deleteTagCategory,
    {
      request: AssetTagsConstants.DELETE_TAG_CATEGORY_REQUEST,
      success: AssetTagsConstants.DELETE_TAG_CATEGORY_REQUEST_SUCCESS,
      failure: AssetTagsConstants.DELETE_TAG_CATEGORY_REQUEST_ERROR,
      payload: { ...payload, prevIndex: index },
    },
    payload.tagCategory.id
  );
}

export function* createTag(action: ReturnType<typeof createTagAction>) {
  const { payload } = action;
  yield* callRequestSaga(
    AssetTagsAPI.createTag,
    {
      request: AssetTagsConstants.CREATE_TAG_REQUEST,
      success: AssetTagsConstants.CREATE_TAG_REQUEST_SUCCESS,
      failure: AssetTagsConstants.CREATE_TAG_REQUEST_ERROR,
      payload: {
        ...payload,
        tmpTag: {
          id: `${Math.floor(Math.random() * 100001)}`,
          name: payload.name,
          tagCategoryId: payload.tagCategory.id,
          usage: 0,
        },
      },
    },
    payload.tagCategory.id,
    payload.name
  );
}

export function* updateTag(action: ReturnType<typeof updateTagAction>) {
  const { payload } = action;

  yield* callRequestSaga(
    AssetTagsAPI.updateTag,
    {
      request: AssetTagsConstants.UPDATE_TAG_REQUEST,
      success: AssetTagsConstants.UPDATE_TAG_REQUEST_SUCCESS,
      failure: AssetTagsConstants.UPDATE_TAG_REQUEST_ERROR,
      payload,
    },
    payload.tag.id,
    payload.name
  );
}

export function* deleteTag(action: ReturnType<typeof deleteTagAction>) {
  const { payload } = action;

  const index = yield select((state: AppState) =>
    state.assetTags.availableTagCategories?.[
      state.assetTags.availableTagCategories.findIndex(
        (cat) => cat.id === payload.tag.tagCategoryId
      )
    ].tags.findIndex((tag) => tag.id === payload.tag.id)
  );
  yield* callRequestSaga(
    AssetTagsAPI.deleteTag,
    {
      request: AssetTagsConstants.DELETE_TAG_REQUEST,
      success: AssetTagsConstants.DELETE_TAG_REQUEST_SUCCESS,
      failure: AssetTagsConstants.DELETE_TAG_REQUEST_ERROR,
      payload: { ...payload, prevIndex: index },
    },
    payload.tag.id
  );
}

export function* getAvailableTagsSaga() {
  yield takeLatest(AssetTagsConstants.GET_AVAILABLE_TAGS, getAvailableTags);
}

export function* createTagCategorySaga() {
  yield takeEvery(AssetTagsConstants.CREATE_TAG_CATEGORY, createTagCategory);
}

export function* updateTagCategorySaga() {
  yield takeEvery(AssetTagsConstants.UPDATE_TAG_CATEGORY, updateTagCategory);
}

export function* deleteTagCategorySaga() {
  yield takeEvery(AssetTagsConstants.DELETE_TAG_CATEGORY, deleteTagCategory);
}

export function* createTagSaga() {
  yield takeLatest(AssetTagsConstants.CREATE_TAG, createTag);
}

export function* updateTagSaga() {
  yield takeEvery(AssetTagsConstants.UPDATE_TAG, updateTag);
}

export function* deleteTagSaga() {
  yield takeEvery(AssetTagsConstants.DELETE_TAG, deleteTag);
}

export default [
  getAvailableTagsSaga,
  createTagCategorySaga,
  updateTagCategorySaga,
  deleteTagCategorySaga,
  createTagSaga,
  updateTagSaga,
  deleteTagSaga,
];
